import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getJson } from "../../../utils/api";
import { AdminEngagementResponse } from "../../../models";
import { ADMIN } from "../utils";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ActivityHeatmap: React.FC<{
  data: { day_of_week: number; hour_of_day: number; completions: number }[];
}> = React.memo(({ data }) => {
  // Fix #14: Memoize grid calculation
  const { grid, maxVal } = useMemo(() => {
    const g: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let max = 1;
    data.forEach((item) => {
      g[item.day_of_week][item.hour_of_day] = item.completions;
      if (item.completions > max) max = item.completions;
    });
    return { grid: g, maxVal: max };
  }, [data]);

  const cellColor = (val: number): string => {
    if (val === 0) return "transparent";
    const intensity = Math.min(val / maxVal, 1);
    const alpha = 0.15 + intensity * 0.7;
    return `rgba(46, 125, 50, ${alpha})`;
  };

  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 40 }}>Day</TableCell>
            {Array.from({ length: 24 }, (_, i) => (
              <TableCell
                key={i}
                align="center"
                sx={{ px: 0.5, minWidth: 28, fontSize: "0.7rem" }}
              >
                {i}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {grid.map((row, dayIdx) => (
            <TableRow key={dayIdx}>
              <TableCell sx={{ fontWeight: 500, fontSize: "0.75rem" }}>
                {DAY_NAMES[dayIdx]}
              </TableCell>
              {row.map((val, hourIdx) => (
                <TableCell
                  key={hourIdx}
                  align="center"
                  sx={{
                    px: 0.25,
                    py: 0.25,
                    backgroundColor: cellColor(val),
                    fontSize: "0.7rem",
                  }}
                >
                  {val > 0 ? val : ""}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

const EngagementTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [engagement, setEngagement] = useState<AdminEngagementResponse | null>(
    null
  );
  const [engagementDays, setEngagementDays] = useState(30);

  const fetchEngagement = useCallback(async (days: number) => {
    try {
      setLoading(true);
      setError(null);
      const resp = await getJson(
        `${ADMIN}/learning/metrics/engagement?days=${days}`
      );
      const { data } = await resp.json();
      setEngagement(data ?? null);
    } catch (e) {
      console.error("Failed to fetch engagement", e);
      setError("Failed to load engagement data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fix #12: proper dependency array
  useEffect(() => {
    fetchEngagement(engagementDays);
  }, [engagementDays, fetchEngagement]);

  if (error) return <Typography color="error">{error}</Typography>;
  if (loading) return <Typography>Loading...</Typography>;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h6">Engagement</Typography>
        <TextField
          select
          size="small"
          label="Period"
          value={engagementDays}
          onChange={(e) => setEngagementDays(Number(e.target.value))}
          sx={{ width: 120 }}
        >
          {[7, 14, 30, 90, 365].map((d) => (
            <MenuItem key={d} value={d}>
              {d} days
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {engagement && (
        <>
          {/* Daily Completions */}
          <Typography variant="subtitle1" sx={{ mb: 0.5, mt: 1 }}>
            Daily Completions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Total lesson completions and unique active users per day, based on
            lesson completion timestamps within the selected period.
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <Tooltip
                    title="Total lesson completions on this day"
                    arrow
                    placement="top"
                  >
                    <TableCell align="right">Completions</TableCell>
                  </Tooltip>
                  <Tooltip
                    title="Unique users who completed at least one lesson on this day"
                    arrow
                    placement="top"
                  >
                    <TableCell align="right">Unique Users</TableCell>
                  </Tooltip>
                </TableRow>
              </TableHead>
              <TableBody>
                {engagement.daily_completions.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell>{row.date}</TableCell>
                    <TableCell align="right">{row.completions}</TableCell>
                    <TableCell align="right">{row.unique_users}</TableCell>
                  </TableRow>
                ))}
                {engagement.daily_completions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No data
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Activity Heatmap */}
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            Activity by Day &amp; Hour (UTC)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Heatmap of lesson completions grouped by day of week and hour (UTC).
            Darker cells indicate higher activity. Helps identify peak learning
            times.
          </Typography>
          <ActivityHeatmap data={engagement.activity_heatmap} />
        </>
      )}
    </>
  );
};

export default React.memo(EngagementTab);
